import { useCallback, useState, useRef } from 'react';
import { useSelector } from 'react-redux';

import { NotificationService } from 'ui-components/services/notification-service';

import { PageEvents, useTrackEvent } from './use-track-event';
import { FIELD_NAMES } from '../../../../address/address-fields/constants';
import { CITY, COMPANY, LINE1, POSTAL_CODE } from '../../../../address/constants';
import { handleFormFieldValidation } from '../../../../services/form-validation-service';
import { createTaxData, updateTaxData } from '../../../../services/tax-identifiers/api';
import Global from '../../../../store/global-selectors';
import { TaxError, TAX_IDENTIFIER_NOTIFICATION, TAX_IDENTIFIER_FIELD } from '../constants/tax-identifier';
import { useTaxDataContext } from '../context/tax-data-context-provider';
import { useTaxIdConfig } from '../hooks/use-tax-id-config';

const isEmptyObject = (object = {}) => Object.values(object).every((val) => !val);

const defaultRequiredFields = [COMPANY, LINE1, CITY, POSTAL_CODE, TAX_IDENTIFIER_FIELD];

/** @typedef {import('../types/tax-types').TaxData} TaxData */
/** @typedef {import('../types/tax-types').InvoiceAddress} InvoiceAddress */
/** @typedef {import('../types/tax-types').UseTaxFormType} UseTaxFormType */
/** @typedef {(field: string, value: string) => void} FormHandlerType */

/**
 * @typedef {object} UseTaxFormArgs
 * @property {string[]} [requiredFields]
 * @property {boolean} [isEditing]
 * @property {string} [source]
 */

/**
 * @param {UseTaxFormArgs} args
 * @returns {UseTaxFormType}
 */
export const useTaxForm = ({ requiredFields = defaultRequiredFields, isEditing: isEditingInitialValue = false, source = '' }) => {
  const notificationId = useRef('');
  const { taxData, setTaxData, isTaxDataAvailable } = useTaxDataContext();
  /** @type {string} */
  const sellerHomeCountry = useSelector(Global.selectSellerHomeCountry);

  const [taxIdentifier, setTaxIdentifier] = useState(taxData?.taxIdentifier || '');
  const [issuingAuthority, setIssuingAuthority] = useState(taxData?.issuingAuthority || '');
  const { taxIdName, isValidTaxId, isInvoiceAddressRequired, defaultIssuingAuthority } = useTaxIdConfig(issuingAuthority);
  const [invoiceAddress, setInvoiceAddress] = useState(
    isEmptyObject(taxData?.invoiceAddress) ? { countryCode: taxData?.issuingAuthority || sellerHomeCountry } : taxData?.invoiceAddress,
  );
  const [fieldsWithError, setFieldsWithError] = useState({});
  const [isEditing, setIsEditing] = useState(isEditingInitialValue);
  const [isTaxSectionOpen, setIsTaxSectionOpen] = useState(false);

  const trackCompletedEvent = useTrackEvent(PageEvents.ADD_VAT_ID_COMPLETE, source);
  const trackCloseEvent = useTrackEvent(PageEvents.ADD_VAT_ID_CLOSE, source);
  const trackErrorEvent = useTrackEvent(PageEvents.ADD_VAT_ID_ERROR, source);

  const showNotification = useCallback(
    ({ title, notificationType, getContent }) => {
      NotificationService.removeNotification(notificationId.current);

      notificationId.current = NotificationService.showNotification({
        title,
        notificationType,
        content: getContent(taxIdName),
        duration: 10000,
      });
    },
    [taxIdName],
  );

  /** @type {FormHandlerType} */
  const isInvalidFormField = useCallback(
    (field, value) => {
      const isRequired = requiredFields.includes(field);
      return isRequired && (handleFormFieldValidation(field, value) || !value);
    },
    [requiredFields],
  );

  /** @type {FormHandlerType} */
  const validateFormField = useCallback(
    (field, value) => {
      const effectiveIssuingAuthority = issuingAuthority || defaultIssuingAuthority;
      setFieldsWithError({
        ...fieldsWithError,
        [field]: field === TAX_IDENTIFIER_FIELD ? !isValidTaxId(value, effectiveIssuingAuthority) : isInvalidFormField(field, value),
      });
    },
    [fieldsWithError, isInvalidFormField, isValidTaxId, issuingAuthority, defaultIssuingAuthority],
  );

  const isInvalidTaxId = useCallback(() => {
    const effectiveIssuingAuthority = issuingAuthority || defaultIssuingAuthority;
    return !isTaxDataAvailable && !isValidTaxId(taxIdentifier, effectiveIssuingAuthority);
  }, [isTaxDataAvailable, isValidTaxId, taxIdentifier, issuingAuthority, defaultIssuingAuthority]);

  const validateForm = useCallback(() => {
    const newFieldsWithError =
      Object.fromEntries(
        requiredFields.map((field) => [
          field,
          field === TAX_IDENTIFIER_FIELD ? isInvalidTaxId() : isInvalidFormField(field, invoiceAddress[field]),
        ]),
      ) || {};
    setFieldsWithError(newFieldsWithError);
    return newFieldsWithError;
  }, [invoiceAddress, isInvalidFormField, isInvalidTaxId, requiredFields]);

  /** @type {FormHandlerType} */
  const setInvoiceAddressField = useCallback(
    (field, value) => {
      setIsEditing(true);
      setInvoiceAddress({
        ...invoiceAddress,
        [field]: value,
      });
    },
    [invoiceAddress],
  );

  const isInvalidForm = useCallback(() => {
    if (!requiredFields.length) return false;
    const errors = validateForm();
    return !isEmptyObject(errors);
  }, [requiredFields, validateForm]);

  /**
   * @returns {Promise<TaxData|void>}
   */
  const submitTaxForm = async () => {
    return new Promise((resolve, reject) => {
      if (isInvalidForm()) {
        reject(new Error(TaxError.InvalidForm));
        return;
      }

      showNotification(TAX_IDENTIFIER_NOTIFICATION.INFO);
      const effectiveIssuingAuthority = issuingAuthority || defaultIssuingAuthority;
      const payload = isInvoiceAddressRequired
        ? { taxIdentifier, issuingAuthority: effectiveIssuingAuthority, invoiceAddress }
        : { taxIdentifier };

      const action = isTaxDataAvailable ? updateTaxData : createTaxData;
      return action(payload)
        .then((data) => {
          trackCompletedEvent();
          showNotification(TAX_IDENTIFIER_NOTIFICATION.SUCCESS);
          setTaxData({ ...taxData, ...data });
          setIsEditing(false);
          resolve(data);
        })
        .catch((error) => {
          trackErrorEvent();
          showNotification(TAX_IDENTIFIER_NOTIFICATION.VALIDATION_ERROR);
          reject(error);
        });
    });
  };

  /** @type {(value: string) => void} */
  const setIssuingAuthorityValue = useCallback(
    (value) => {
      setIssuingAuthority(value);
      setInvoiceAddressField(FIELD_NAMES.COUNTRY_CODE, value);
      setIsEditing(true);
    },
    [setInvoiceAddressField],
  );

  const clearFormData = useCallback(() => {
    setTaxIdentifier(taxData?.taxIdentifier || '');
    setIssuingAuthority(taxData?.issuingAuthority || '');
    setInvoiceAddress(
      isEmptyObject(taxData?.invoiceAddress) ? { countryCode: taxData?.issuingAuthority || sellerHomeCountry } : taxData?.invoiceAddress,
    );
    setFieldsWithError({});
    setIsEditing(false);
    setIsTaxSectionOpen(false);
  }, [sellerHomeCountry, taxData]);

  /** @type {(value: string) => void} */
  const setTaxIdentifierValue = useCallback((value) => {
    setIsEditing(true);
    setTaxIdentifier(value);
  }, []);

  const closeProcess = useCallback(() => {
    trackCloseEvent();
    clearFormData();
  }, [clearFormData, trackCloseEvent]);

  return {
    taxIdentifier,
    setTaxIdentifier: setTaxIdentifierValue,
    issuingAuthority,
    setIssuingAuthorityValue,
    setInvoiceAddressField,
    invoiceAddress,
    fieldsWithError,
    validateFormField,
    submitTaxForm,
    isInvalidForm,
    isEditing,
    closeProcess,
    isTaxSectionOpen,
    setIsTaxSectionOpen,
  };
};
